import React from 'react'
import { Skeleton } from '@acctglobal/skeleton'
import { Helmet } from 'react-helmet'
import { useSearch } from '@faststore/sdk'
import type {
  IStoreSelectedFacet,
  ProductGalleryQueryQuery,
} from '@generated/graphql'
import LinkButton from 'src/components/ui/Button/LinkButton'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import { widthSkeletonProductGrid } from 'src/components/product/ProductGrid/ProductGrid'

import GalleryPage from './ProductGalleryPage'

import './product-gallery.scss'

interface ProductGalleryAboveProps {
  prev:
    | false
    | {
        cursor: number
        link: string
      }
  next:
    | false
    | {
        cursor: number
        link: string
      }
  data: ProductGalleryQueryQuery | undefined
  title: string
  isLayoutList: boolean
  setIsButtonLoading: React.Dispatch<React.SetStateAction<boolean>>
  isButtonLoading: boolean
  selectedFacets: IStoreSelectedFacet[]
}

const ProductGalleryBelow = ({
  prev,
  next,
  data,
  title,
  isLayoutList,
  setIsButtonLoading,
  isButtonLoading,
  selectedFacets,
}: ProductGalleryAboveProps) => {
  const { width } = useWindowDimensions()

  const { pages, state: searchState, addNextPage, addPrevPage } = useSearch()

  return (
    <div className="product-listing__results">
      {data ? (
        <>
          {/* Add link to prev page. This helps on SEO */}
          {prev !== false && (
            <div className="product-listing__pagination product-listing__pagination--top">
              <Helmet>
                <link rel="prev" href={prev.link} data-rh="true" />
              </Helmet>
              <LinkButton
                className="previous-items"
                data-testid="show-previous"
                onClick={(e) => {
                  e.currentTarget.blur()
                  e.preventDefault()
                  addPrevPage()
                }}
                to={prev.link}
                rel="prev"
                variant="tertiary"
              >
                CARREGAR PRODUTOS ANTERIORES
              </LinkButton>
            </div>
          )}
          {/* Render ALL products */}
          {pages?.map((page) => (
            <GalleryPage
              key={`gallery-page-${page}`}
              showSponsoredProducts={false}
              fallbackData={page === searchState.page ? data : undefined}
              page={page}
              title={title}
              layoutGridOrList={isLayoutList}
              setIsButtonLoading={setIsButtonLoading}
              isLoading
              selectedFacets={selectedFacets}
            />
          ))}
          {/* Prefetch Previous and Next pages */}
          {prev !== false && (
            <GalleryPage
              showSponsoredProducts={false}
              page={prev.cursor}
              display={false}
              title={title}
              layoutGridOrList={isLayoutList}
              selectedFacets={selectedFacets}
            />
          )}
          {next !== false && (
            <GalleryPage
              showSponsoredProducts={false}
              page={next.cursor}
              display={false}
              title={title}
              layoutGridOrList={isLayoutList}
              selectedFacets={selectedFacets}
            />
          )}

          {/* Add link to next page. This helps on SEO */}
          {next !== false && (
            <div className="product-listing__pagination product-listing__pagination--bottom">
              <Helmet>
                <link rel="next" href={next.link} data-rh="true" />
              </Helmet>
              <LinkButton
                className="more-items"
                data-testid="show-more"
                onClick={(e) => {
                  e.currentTarget.blur()
                  e.preventDefault()
                  setIsButtonLoading(true)
                  addNextPage()
                }}
                to={next.link}
                rel="next"
                variant="secondary"
              >
                {isButtonLoading ? 'CARREGANDO...' : 'CARREGAR MAIS PRODUTOS'}
              </LinkButton>
            </div>
          )}
        </>
      ) : (
        <Skeleton
          table={widthSkeletonProductGrid(width)}
          width={width ? (width <= 1200 ? width : 1224) : undefined}
          height={400}
          backgroundColor="#F4F4F4"
        />
      )}
    </div>
  )
}

export default ProductGalleryBelow
